import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Star, ChevronRight, LogIn } from "lucide-react";
import { useSiteSettings } from "@/context/SiteSettingsContext";
import { T } from "@/context/TextEditContext";
import { loyaltyKeyParam, rememberLoyaltyKey } from "@/lib/loyaltyKey";

const STORAGE_KEY = "bk_loyalty_phone";

function fmtPhone(digits) {
  const d = (digits || "").replace(/\D/g, "").slice(0, 10);
  if (d.length < 4) return d;
  if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

/** HomeLoyaltyCard — prominent sign-in / balance card on the customer home
 * screen. When signed in it greets the customer and shows their current
 * points; otherwise it renders a one-tap phone sign-in form. */
export default function HomeLoyaltyCard() {
  const [phone, setPhone] = useState(() => localStorage.getItem(STORAGE_KEY) || "");
  const [data, setData] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [inputPhone, setInputPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const { settings } = useSiteSettings();

  useEffect(() => {
    if (!phone) {
      setData(null);
      return;
    }
    api.get(`/loyalty/${phone}${loyaltyKeyParam()}`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [phone]);

  const signIn = async () => {
    const digits = inputPhone.replace(/\D/g, "");
    if (digits.length < 7) {
      toast.error("Enter a valid phone number");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/loyalty/signup", { phone: digits });
      rememberLoyaltyKey(data);
      localStorage.setItem(STORAGE_KEY, digits);
      setPhone(digits);
      setData(data);
      setShowForm(false);
      setInputPhone("");
      toast.success(data.exists ? "Welcome back!" : "You're in!");
    } catch {
      toast.error("Sign-in failed");
    } finally {
      setBusy(false);
    }
  };

  // Signed-in state
  if (phone && data) {
    // Ladder is derived from the backend rewards (sorted by points ascending)
    // so admin-added rewards automatically appear in the "next reward" chip.
    const ladder = (data.rewards || [])
      .slice()
      .sort((a, b) => a.points - b.points);
    const upcoming =
      ladder.find((t) => data.points < t.points) || ladder[ladder.length - 1];
    const nextTarget = upcoming ? upcoming.points : 50;
    const nextLabel = upcoming ? upcoming.label : "Free Drink";
    const toGo = Math.max(0, nextTarget - data.points);
    const pct = Math.min(100, (data.points / nextTarget) * 100);
    return (
      <Link
        to="/rewards"
        data-testid="home-loyalty-card-signed-in"
        className="mx-4 mt-3 block bg-white border-2 border-brand-red rounded-2xl p-4 shadow-md hover:shadow-lg active:scale-[0.99] transition-all"
      >
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-brand-red flex items-center justify-center shrink-0">
            <Star className="w-6 h-6 text-brand-yellow" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs uppercase tracking-widest text-stone-500 font-black">
              {data.name ? `Hi ${data.name}` : <T k="loyalty.card_title" d="Blossom's Rewards" />}
            </div>
            <div className="text-3xl font-display leading-none mt-1 text-brand-red">
              {data.points}{" "}
              <span className="text-base text-stone-500 font-bold uppercase tracking-widest">
                pts
              </span>
            </div>
          </div>
          <ChevronRight className="w-6 h-6 text-stone-400" />
        </div>
        <div className="mt-4 space-y-1.5">
          <div className="flex justify-between text-xs font-black text-stone-800">
            <span>
              {toGo > 0 ? `${toGo} pts to ${nextLabel}` : `${nextLabel} UNLOCKED!`}
            </span>
            <span className="text-stone-500">
              {data.points}/{nextTarget}
            </span>
          </div>
          <div className="h-2.5 bg-stone-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-brand-red transition-all"
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>
      </Link>
    );
  }

  // Not-signed-in state — collapsed pill or inline form
  if (showForm) {
    return (
      <div
        data-testid="home-loyalty-signin-form"
        className="mx-4 mt-3 bg-white border-2 border-brand-red rounded-2xl p-4 space-y-3 animate-float-in shadow-md"
      >
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-brand-red" />
          <div className="text-sm font-bold text-stone-900">
            <T k="loyalty.signin_title" d="Sign in to Blossom's Rewards" />
          </div>
        </div>
        <input
          type="tel"
          inputMode="tel"
          value={fmtPhone(inputPhone)}
          onChange={(e) => setInputPhone(e.target.value.replace(/\D/g, ""))}
          placeholder="4795551234 or (479) 555-1234"
          data-testid="home-loyalty-phone-input"
          className="w-full px-3 py-2.5 rounded-lg border-2 border-stone-300 bg-white outline-none focus:border-brand-red text-sm font-mono"
          autoFocus
        />
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => {
              setShowForm(false);
              setInputPhone("");
            }}
            className="flex-1 bg-white border-2 border-stone-300 text-stone-600 py-2 rounded-full text-xs uppercase tracking-widest font-bold hover:border-stone-500"
          >
            <T k="loyalty.signin_cancel" d="Cancel" />
          </button>
          <button
            type="button"
            onClick={signIn}
            disabled={busy || inputPhone.replace(/\D/g, "").length < 7}
            data-testid="home-loyalty-signin-submit"
            className="flex-1 bg-brand-red text-white py-2 rounded-full text-xs uppercase tracking-widest font-bold hover:bg-red-800 disabled:opacity-50"
          >
            {busy ? "..." : <T k="loyalty.signin_btn" d="Sign In" />}
          </button>
        </div>
        <div className="text-[11px] text-stone-500 text-center">
          <T k="loyalty.new_here" d="New here?" />{" "}
          <Link to="/rewards" className="text-brand-red font-bold underline">
            <T k="loyalty.signup_link" d="Sign up on the rewards page" />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setShowForm(true)}
      data-testid="home-loyalty-signin-cta"
      className="mx-4 mt-3 w-[calc(100%-2rem)] bg-white border-2 border-brand-red rounded-2xl p-3 flex items-center gap-3 hover:bg-red-50 transition-colors shadow-md"
    >
      <div className="w-10 h-10 rounded-full bg-brand-red flex items-center justify-center shrink-0">
        <Star className="w-5 h-5 text-brand-yellow" />
      </div>
      <div className="flex-1 text-left">
        <div className="text-sm font-bold text-stone-900 leading-tight">
          <T field="loyalty_card_headline" />
        </div>
        <div className="text-[11px] text-stone-600">
          <T field="loyalty_card_subtext" />
        </div>
      </div>
      <div className="flex items-center gap-1.5 bg-brand-red text-white px-3 py-1.5 rounded-full text-[10px] uppercase tracking-widest font-black shrink-0">
        <LogIn className="w-3 h-3" />
        <T k="loyalty.signin_pill" d="Sign In" />
      </div>
    </button>
  );
}
